import { useEffect, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { Link } from 'react-router-dom';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import type { ManagerRole } from '../api/managerApi';
import {
  DataTable,
  DurationInput,
  LoadingState,
  inlineFieldSx,
  useErrorToast,
  type DataTableColumn,
} from '../components/common';
import { useEventContext } from '../contexts/EventContext';
import { buildEventScopedPath } from '../routes/appRoutes';
import { categoryService, type Category } from '../services/categoryService';
import { loadMyManagerProfile } from '../services/managerService';
import { timingPointService, type TimingPoint } from '../services/timingPointService';
import { splitService, type Split, type SplitUpdateBody } from '../services/splitService';

/** 소요시간 입력('-?H:mm' 또는 '-?H:mm:ss')을 부호 있는 초로 파싱. 형식 오류면 null */
function parseDurationToSeconds(value: string): number | null {
  const matched = /^(-)?(\d{1,3}):([0-5]?\d)(?::([0-5]?\d))?$/.exec(value.trim());
  if (!matched) return null;
  const sign = matched[1] ? -1 : 1;
  const hours = Number(matched[2]);
  const minutes = Number(matched[3]);
  const seconds = Number(matched[4] ?? 0);
  return sign * (hours * 3600 + minutes * 60 + seconds);
}

/** 입력을 정규화된 '-?HH:mm:ss' 문자열로. 형식 오류면 null */
function toCanonicalDuration(value: string): string | null {
  const total = parseDurationToSeconds(value);
  if (total === null) return null;
  const sign = total < 0 ? '-' : '';
  let remain = Math.abs(total);
  const hours = Math.floor(remain / 3600);
  remain %= 3600;
  const minutes = Math.floor(remain / 60);
  const seconds = remain % 60;
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${sign}${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
}

function submitKeys(confirm: () => void, cancel: () => void) {
  return (e: ReactKeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      confirm();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      cancel();
    }
  };
}

function getNextSelectedCategoryId(current: number | null, list: Category[]) {
  if (current != null && list.some((category) => category.id === current)) {
    return current;
  }
  return list[0]?.id ?? null;
}

const tabularNum = { fontVariantNumeric: 'tabular-nums' } as const;
const cellFieldSx = { ...inlineFieldSx, fontSize: 12, height: 30 };

const iconStroke = {
  width: 15,
  height: 15,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
};

const TrashIcon = () => (
  <svg {...iconStroke}>
    <path d="M3 6h18" />
    <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6" />
  </svg>
);

const EditIcon = () => (
  <svg {...iconStroke}>
    <path d="M12 20h9" />
    <path d="M16.5 3.5a2.12 2.12 0 0 1 3 3L7 19l-4 1 1-4Z" />
  </svg>
);

/** 구간 폼 입력값 */
type SplitFormInput = {
  name: string;
  timingPointId: number | '';
  splitDistance: string;
  totalDistance: string;
  minTime: string;
  maxTime: string;
};

/** 폼 입력을 검증·정규화해 전송 본문으로 변환. 실패 시 에러 메시지를 반환한다. */
function buildSplitBody(input: SplitFormInput): { error: string } | { body: SplitUpdateBody } {
  const name = input.name.trim();
  if (!name || input.timingPointId === '' || input.splitDistance === '' || input.totalDistance === '') {
    return { error: '이름·종료지점·거리를 입력해 주세요.' };
  }
  const distance = Number(input.splitDistance);
  const total = Number(input.totalDistance);
  if (Number.isNaN(distance) || Number.isNaN(total) || distance < 0 || total < 0) {
    return { error: '거리는 0 이상의 숫자여야 합니다.' };
  }
  // 시간제한은 선택값 — 입력된 것만 검증, 비우면 null(제한 없음)
  let minTime: string | null = null;
  let maxTime: string | null = null;
  if (input.minTime.trim()) {
    if (parseDurationToSeconds(input.minTime) === null) {
      return { error: '시간제한 형식이 올바르지 않습니다. (예: -00:00:05, 00:15:00)' };
    }
    minTime = toCanonicalDuration(input.minTime);
  }
  if (input.maxTime.trim()) {
    if (parseDurationToSeconds(input.maxTime) === null) {
      return { error: '시간제한 형식이 올바르지 않습니다. (예: -00:00:05, 00:15:00)' };
    }
    maxTime = toCanonicalDuration(input.maxTime);
  }
  if (
    minTime !== null &&
    maxTime !== null &&
    (parseDurationToSeconds(minTime) as number) >= (parseDurationToSeconds(maxTime) as number)
  ) {
    return { error: '시간제한 최소는 최대보다 작아야 합니다.' };
  }
  return {
    body: {
      timingPointId: input.timingPointId,
      splitName: name,
      splitDistance: distance,
      totalDistance: total,
      minTime,
      maxTime,
    },
  };
}

function SplitsPage() {
  const { selectedEventId } = useEventContext();
  const [categories, setCategories] = useState<Category[]>([]);
  const [timingPoints, setTimingPoints] = useState<TimingPoint[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);
  const [splits, setSplits] = useState<Split[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isSplitsLoading, setIsSplitsLoading] = useState(false);
  const [managerRole, setManagerRole] = useState<ManagerRole | null>(null);
  const { showError, errorToast } = useErrorToast();
  const canManageSplits = managerRole === 'SUPER' || managerRole === 'MIDDLE';

  // 구간 추가 상태
  const [isAdding, setIsAdding] = useState(false);
  const [newName, setNewName] = useState('');
  const [newTimingPointId, setNewTimingPointId] = useState<number | ''>('');
  const [newSplitDistance, setNewSplitDistance] = useState('');
  const [newTotalDistance, setNewTotalDistance] = useState('');
  const [newMinTime, setNewMinTime] = useState('');
  const [newMaxTime, setNewMaxTime] = useState('');

  // 구간 인라인 수정 상태
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editTimingPointId, setEditTimingPointId] = useState<number | ''>('');
  const [editSplitDistance, setEditSplitDistance] = useState('');
  const [editTotalDistance, setEditTotalDistance] = useState('');
  const [editMinTime, setEditMinTime] = useState('');
  const [editMaxTime, setEditMaxTime] = useState('');

  useEffect(() => {
    let ignore = false;

    loadMyManagerProfile()
      .then((profile) => {
        if (!ignore) {
          setManagerRole(profile.role);
        }
      })
      .catch(() => {
        if (!ignore) {
          setManagerRole(null);
        }
      });

    return () => {
      ignore = true;
    };
  }, []);

  useEffect(() => {
    if (selectedEventId == null) return;

    const loadInitialData = async () => {
      setIsInitialLoading(true);
      try {
        const [list, timingPointsList] = await Promise.all([
          categoryService.list(selectedEventId),
          timingPointService.list(selectedEventId),
        ]);
        setCategories(list);
        setSelectedCategoryId((current) => getNextSelectedCategoryId(current, list));
        setTimingPoints(timingPointsList);
      } catch (error) {
        showError(error);
      } finally {
        setIsInitialLoading(false);
      }
    };

    void loadInitialData();
  }, [selectedEventId, showError]);

  useEffect(() => {
    if (selectedEventId == null || selectedCategoryId == null) {
      setSplits([]);
      return;
    }
    setIsSplitsLoading(true);
    splitService
      .list(selectedEventId, selectedCategoryId)
      .then(setSplits)
      .catch(showError)
      .finally(() => setIsSplitsLoading(false));
  }, [selectedEventId, selectedCategoryId, showError]);

  if (selectedEventId == null) {
    return (
      <section className="page-placeholder">
        <div className="page-content page-content-wide">
          <p className="page-section">Event</p>
          <h1>구간 관리</h1>
          <Box sx={{ mt: 3, color: '#8a94a6', fontSize: 14 }}>
            선택된 대회가 없습니다. 대회 목록에서 대회를 먼저 선택해 주세요.
          </Box>
        </div>
      </section>
    );
  }

  const selectedCategory = categories.find((c) => c.id === selectedCategoryId) ?? null;
  const timingPointName = (id: number) =>
    timingPoints.find((tp) => tp.id === id)?.timingPointName ?? '—';

  const selectCategory = (id: number) => {
    setSelectedCategoryId(id);
    setIsAdding(false);
    setEditingId(null);
    setError(null);
  };

  const resetAddForm = () => {
    setNewName('');
    setNewTimingPointId('');
    setNewSplitDistance('');
    setNewTotalDistance('');
    setNewMinTime('');
    setNewMaxTime('');
  };

  const cancelAdd = () => {
    resetAddForm();
    setIsAdding(false);
    setError(null);
  };

  const handleAddConfirm = async () => {
    if (!canManageSplits) return;
    if (selectedCategory == null) return;
    const result = buildSplitBody({
      name: newName,
      timingPointId: newTimingPointId,
      splitDistance: newSplitDistance,
      totalDistance: newTotalDistance,
      minTime: newMinTime,
      maxTime: newMaxTime,
    });
    if ('error' in result) {
      setError(result.error);
      return;
    }

    try {
      const created = await splitService.create(selectedEventId, {
        categoryId: selectedCategory.id,
        ...result.body,
      });
      setSplits((prev) => [...prev, created]);
      resetAddForm();
      setIsAdding(false);
      setError(null);
    } catch (err) {
      showError(err);
    }
  };

  const startEdit = (split: Split) => {
    if (!canManageSplits) return;
    setIsAdding(false);
    setError(null);
    setEditingId(split.id);
    setEditName(split.splitName);
    setEditTimingPointId(split.timingPointId);
    setEditSplitDistance(String(split.splitDistance));
    setEditTotalDistance(String(split.totalDistance));
    setEditMinTime(split.minTime ?? '');
    setEditMaxTime(split.maxTime ?? '');
  };

  const cancelEdit = () => {
    setEditingId(null);
    setError(null);
  };

  const handleEditConfirm = async () => {
    if (!canManageSplits) return;
    if (editingId == null) return;
    const result = buildSplitBody({
      name: editName,
      timingPointId: editTimingPointId,
      splitDistance: editSplitDistance,
      totalDistance: editTotalDistance,
      minTime: editMinTime,
      maxTime: editMaxTime,
    });
    if ('error' in result) {
      setError(result.error);
      return;
    }

    try {
      const updated = await splitService.update(selectedEventId, editingId, result.body);
      setSplits((prev) => prev.map((s) => (s.id === updated.id ? updated : s)));
      setEditingId(null);
      setError(null);
    } catch (err) {
      showError(err);
    }
  };

  const handleDelete = async (split: Split) => {
    if (!canManageSplits) return;
    try {
      await splitService.delete(selectedEventId, split.id);
      setSplits((prev) => prev.filter((s) => s.id !== split.id));
      setError(null);
    } catch (err) {
      showError(err);
    }
  };

  const timingPointOptions = timingPoints.map((tp) => (
    <MenuItem key={tp.id} value={String(tp.id)} sx={{ fontSize: 12 }}>
      {tp.timingPointName}
    </MenuItem>
  ));

  const columns: DataTableColumn<Split>[] = [
    {
      key: 'splitName',
      header: '이름',
      render: (row) =>
        row.id === editingId ? (
          <InputBase
            autoFocus
            fullWidth
            value={editName}
            onChange={(e) => setEditName(e.target.value)}
            onKeyDown={submitKeys(handleEditConfirm, cancelEdit)}
            sx={cellFieldSx}
          />
        ) : (
          <span style={{ fontWeight: 600 }}>{row.splitName}</span>
        ),
    },
    {
      key: 'timingPointId',
      header: '종료지점',
      render: (row) =>
        row.id === editingId ? (
          <Select
            size="small"
            displayEmpty
            value={editTimingPointId === '' ? '' : String(editTimingPointId)}
            onChange={(e) => setEditTimingPointId(e.target.value === '' ? '' : Number(e.target.value))}
            sx={{ fontSize: 12, height: 30, minWidth: 120, backgroundColor: '#fff' }}
          >
            <MenuItem value="" disabled sx={{ fontSize: 12 }}>
              종료지점 선택
            </MenuItem>
            {timingPointOptions}
          </Select>
        ) : (
          timingPointName(row.timingPointId)
        ),
    },
    {
      key: 'splitDistance',
      header: '구간거리 (m)',
      width: 130,
      render: (row) =>
        row.id === editingId ? (
          <InputBase
            type="number"
            fullWidth
            value={editSplitDistance}
            onChange={(e) => setEditSplitDistance(e.target.value)}
            onKeyDown={submitKeys(handleEditConfirm, cancelEdit)}
            inputProps={{ min: 0 }}
            sx={cellFieldSx}
          />
        ) : (
          <span style={tabularNum}>{row.splitDistance.toLocaleString()}</span>
        ),
    },
    {
      key: 'totalDistance',
      header: '총거리 (m)',
      width: 130,
      render: (row) =>
        row.id === editingId ? (
          <InputBase
            type="number"
            fullWidth
            value={editTotalDistance}
            onChange={(e) => setEditTotalDistance(e.target.value)}
            onKeyDown={submitKeys(handleEditConfirm, cancelEdit)}
            inputProps={{ min: 0 }}
            sx={cellFieldSx}
          />
        ) : (
          <span style={tabularNum}>{row.totalDistance.toLocaleString()}</span>
        ),
    },
    {
      key: 'timeLimit',
      header: '시간제한 (최소 / 최대)',
      width: 150,
      render: (row) => {
        if (row.id === editingId) {
          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              <DurationInput
                placeholder="-00:00:20 (선택)"
                value={editMinTime}
                onChange={setEditMinTime}
                onKeyDown={submitKeys(handleEditConfirm, cancelEdit)}
                sx={{ ...cellFieldSx, ...tabularNum }}
              />
              <DurationInput
                placeholder="00:15:00 (선택)"
                value={editMaxTime}
                onChange={setEditMaxTime}
                onKeyDown={submitKeys(handleEditConfirm, cancelEdit)}
                sx={{ ...cellFieldSx, ...tabularNum }}
              />
            </Box>
          );
        }
        if (row.minTime == null && row.maxTime == null) {
          return <span style={{ color: '#94a3b8' }}>제한 없음</span>;
        }
        return (
          <Box sx={{ ...tabularNum, lineHeight: 1.45 }}>
            <div>{row.minTime ?? '—'}</div>
            <div style={{ color: '#94a3b8' }}>{row.maxTime ?? '—'}</div>
          </Box>
        );
      },
    },
    ...(canManageSplits
      ? [
          {
            key: '_actions',
            header: '',
            width: 88,
            align: 'right' as const,
            render: (row: Split) => {
              if (row.id === editingId) {
                return (
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" color="primary" onClick={handleEditConfirm}>
                      ✓
                    </IconButton>
                    <IconButton size="small" onClick={cancelEdit}>
                      ✕
                    </IconButton>
                  </Box>
                );
              }
              return (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', color: '#94a3b8' }}>
                  <IconButton
                    size="small"
                    aria-label="구간 수정"
                    sx={{ color: 'inherit', '&:hover': { color: '#2563eb' } }}
                    onClick={() => startEdit(row)}
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    aria-label="구간 삭제"
                    sx={{ color: 'inherit', '&:hover': { color: '#dc2626' } }}
                    onClick={() => handleDelete(row)}
                  >
                    <TrashIcon />
                  </IconButton>
                </Box>
              );
            },
          },
        ]
      : []),
  ];

  const addRow = canManageSplits && isAdding ? (
    <TableRow sx={{ backgroundColor: '#f8fafc', verticalAlign: 'top' }}>
      <TableCell>
        <InputBase
          autoFocus
          fullWidth
          placeholder="구간 이름"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={submitKeys(handleAddConfirm, cancelAdd)}
          sx={cellFieldSx}
        />
      </TableCell>
      <TableCell>
        <Select
          size="small"
          displayEmpty
          value={newTimingPointId === '' ? '' : String(newTimingPointId)}
          onChange={(e) => setNewTimingPointId(e.target.value === '' ? '' : Number(e.target.value))}
          sx={{ fontSize: 12, height: 30, minWidth: 120, backgroundColor: '#fff' }}
        >
          <MenuItem value="" disabled sx={{ fontSize: 12 }}>
            종료지점 선택
          </MenuItem>
          {timingPoints.map((tp) => (
            <MenuItem key={tp.id} value={String(tp.id)} sx={{ fontSize: 12 }}>
              {tp.timingPointName}
            </MenuItem>
          ))}
        </Select>
      </TableCell>
      <TableCell>
        <InputBase
          type="number"
          fullWidth
          placeholder="0"
          value={newSplitDistance}
          onChange={(e) => setNewSplitDistance(e.target.value)}
          onKeyDown={submitKeys(handleAddConfirm, cancelAdd)}
          inputProps={{ min: 0 }}
          sx={cellFieldSx}
        />
      </TableCell>
      <TableCell>
        <InputBase
          type="number"
          fullWidth
          placeholder="0"
          value={newTotalDistance}
          onChange={(e) => setNewTotalDistance(e.target.value)}
          onKeyDown={submitKeys(handleAddConfirm, cancelAdd)}
          inputProps={{ min: 0 }}
          sx={cellFieldSx}
        />
      </TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
          <DurationInput
            placeholder="-00:00:20 (선택)"
            value={newMinTime}
            onChange={setNewMinTime}
            onKeyDown={submitKeys(handleAddConfirm, cancelAdd)}
            sx={{ ...cellFieldSx, ...tabularNum }}
          />
          <DurationInput
            placeholder="00:15:00 (선택)"
            value={newMaxTime}
            onChange={setNewMaxTime}
            onKeyDown={submitKeys(handleAddConfirm, cancelAdd)}
            sx={{ ...cellFieldSx, ...tabularNum }}
          />
        </Box>
      </TableCell>
      <TableCell align="right">
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <IconButton size="small" color="primary" onClick={handleAddConfirm}>
            ✓
          </IconButton>
          <IconButton size="small" onClick={cancelAdd}>
            ✕
          </IconButton>
        </Box>
      </TableCell>
    </TableRow>
  ) : null;

  const hasTimingPoints = timingPoints.length > 0;
  const emptyCategoryContent = (
    <Box
      sx={{
        minHeight: 260,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 1,
        px: 3,
        py: 6,
        border: '1px solid #e5e7eb',
        borderRadius: 2,
        backgroundColor: '#fbfcfe',
        textAlign: 'center',
      }}
    >
      <Box sx={{ fontSize: 16, fontWeight: 700, color: '#1f2937' }}>
        먼저 종목을 등록해 주세요
      </Box>
      <Box sx={{ fontSize: 13, color: '#8a94a6', mb: 2 }}>
        구간은 종목별로 설정합니다. 종목 관리에서 종목을 먼저 추가하세요.
      </Box>
      <Button
        component={Link}
        disableElevation
        size="small"
        to={buildEventScopedPath('/events/categories', selectedEventId)}
        variant="contained"
      >
        종목 관리로 이동
      </Button>
    </Box>
  );
  const splitTableContent = isSplitsLoading ? (
    <LoadingState message="구간 목록을 불러오는 중입니다." />
  ) : (
    <DataTable
      columns={columns}
      rows={splits}
      rowKey="id"
      footer={addRow}
      emptyMessage="등록된 구간이 없습니다. ‘구간 추가’로 통과 구간을 만드세요."
    />
  );
  let mainContent = (
    <>
      {/* 상단 종목 탭 */}
      <Box sx={{ borderBottom: '1px solid #e5e7eb', mb: 2 }}>
        <Tabs
          value={selectedCategoryId ?? false}
          onChange={(_, value) => selectCategory(value as number)}
          variant="standard"
          sx={{
            minHeight: 44,
            '& .MuiTabs-flexContainer': { flexWrap: 'wrap' },
            '& .MuiTabs-indicator': { backgroundColor: '#2563eb' },
          }}
        >
          {categories.map((category) => (
            <Tab
              key={category.id}
              value={category.id}
              disableRipple
              label={category.categoryName}
              sx={{
                textTransform: 'none',
                minHeight: 44,
                fontSize: 14,
                color: '#64748b',
                '&.Mui-selected': { color: '#1d4ed8' },
              }}
            />
          ))}
        </Tabs>
      </Box>

      {/* 헤더 툴바 */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          mb: 2,
        }}
      >
        <Box sx={{ fontSize: 13, color: '#8a94a6' }}>
          {selectedCategory?.categoryName} · 구간 {splits.length}개
        </Box>
        {canManageSplits && (
          <Button
            variant="contained"
            size="small"
            disableElevation
            sx={{ flexShrink: 0, fontWeight: 700 }}
            onClick={() => {
              setError(null);
              setEditingId(null);
              setIsAdding(true);
            }}
            disabled={isAdding || !hasTimingPoints}
          >
            + 구간 추가
          </Button>
        )}
      </Box>

      {!hasTimingPoints && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.25,
            border: '1px solid #fde68a',
            backgroundColor: '#fffbeb',
            borderRadius: 1,
            fontSize: 13,
            color: '#92400e',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}
        >
          <span>종료지점으로 쓸 계측 지점가 없습니다. 먼저 등록해 주세요.</span>
          <Button
            component={Link}
            size="small"
            sx={{ flexShrink: 0 }}
            to={buildEventScopedPath('/events/timing-points', selectedEventId)}
          >
              계측 지점 관리
          </Button>
        </Box>
      )}

      {error && (
        <Box
          sx={{
            mb: 2,
            px: 2,
            py: 1.25,
            border: '1px solid #fecaca',
            backgroundColor: '#fef2f2',
            borderRadius: 1,
            fontSize: 13,
            color: '#b91c1c',
          }}
        >
          {error}
        </Box>
      )}

      {splitTableContent}
    </>
  );

  if (isInitialLoading) {
    mainContent = <LoadingState message="구간 관리 정보를 불러오는 중입니다." />;
  } else if (categories.length === 0) {
    mainContent = emptyCategoryContent;
  }

  return (
    <section className="page-placeholder">
      <div className="page-content page-content-wide">
        <p className="page-section">Event</p>
        <h1>구간 관리</h1>
        <Box className="page-description" sx={{ mt: 0.75 }}>
          구간(Splits)은 출발부터 도착까지의 코스를 나눈 통과 지점 단위입니다. 종목별 계측 지점(Timing Point)과 시간제한을 설정합니다.
        </Box>

        <Box sx={{ mt: 3 }}>
          {mainContent}
        </Box>
      </div>
      {errorToast}
    </section>
  );
}

export default SplitsPage;
